import { useMemo, useState } from 'react';

import { TaskResponse } from '../client';
import { RandomNumberGenerator } from '../utils';
import { useTaskCompletedEffect } from './useTaskCompletedEffect';

interface MemoryMiniGame {
  cards: CardState[];
  revealCard: (index: number) => void;
}

export interface CardState {
  content: string;
  isRevealed: boolean;
  isMatched: boolean;
}

export function useMemoryMiniGame(
  task: TaskResponse,
  onTaskStarted: (task: TaskResponse) => Promise<void>,
  onTaskProgress: (task: TaskResponse, progress: number) => void,
  onTaskCompleted: (task: TaskResponse) => Promise<void>,
): MemoryMiniGame {
  useTaskCompletedEffect(task, onTaskCompleted);
  const [cards, setCards] = useGenerateMemoryGameCards(task.total_effort - task.completed_effort, task.id);
  const [revealedIndices, setRevealedIndices] = useState<number[]>([]); // Currently revealed but unmatched cards

  const revealCard = (index: number) => {
    if (task.status === 'new') {
      onTaskStarted(task);
    }

    if (cards[index].isRevealed || cards[index].isMatched || revealedIndices.length >= 2) {
      return; // Prevent revealing if card is already revealed/matched or 2 cards are already revealed
    }

    // Mark the chosen card as revealed
    const updatedRevealedIndices = [...revealedIndices, index];
    setRevealedIndices(updatedRevealedIndices);

    const updatedCards = cards.map((card, idx) => (idx === index ? { ...card, isRevealed: true } : card));

    setCards(updatedCards);

    // Check for matches if this is the second card revealed
    if (updatedRevealedIndices.length === 2) {
      const [firstIndex, secondIndex] = updatedRevealedIndices;
      const firstCard = cards[firstIndex];
      const secondCard = cards[secondIndex];

      // If the cards match, mark them as matched
      if (firstCard.content === secondCard.content) {
        const newCards = updatedCards.map((card, idx) =>
          idx === firstIndex || idx === secondIndex ? { ...card, isMatched: true } : card,
        );
        setCards(newCards);
        setRevealedIndices([]); // Reset revealed indices
      } else {
        // Reset unmatched revealed cards after a delay
        setTimeout(() => {
          const resetCards = updatedCards.map((card, idx) =>
            idx === firstIndex || idx === secondIndex ? { ...card, isRevealed: false } : card,
          );
          setCards(resetCards);
          setRevealedIndices([]);
        }, 1000);
      }
    }

    // Check if all cards are matched
    if (updatedCards.every((card) => card.isMatched || card.isRevealed)) {
      onTaskProgress(task, task.total_effort);
    }
  };

  return { cards, revealCard };
}

function useGenerateMemoryGameCards(
  size: number,
  seed: string,
): [CardState[], React.Dispatch<React.SetStateAction<CardState[]>>] {
  const cardContents = useTranslateTaskSizeToMemoryGameCards(size, seed);
  const [cards, setCards] = useState<CardState[]>(
    cardContents.map((content) => ({
      content,
      isRevealed: false,
      isMatched: false,
    })),
  );

  return [cards, setCards];
}

function useTranslateTaskSizeToMemoryGameCards(
  size: number,
  seed: string,
  minPairs: number = 2,
  maxPairs: number = 8,
): string[] {
  const alphanumericCharacters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

  return useMemo(() => {
    const rng = new RandomNumberGenerator(seed);
    const clampedSize = Math.min(Math.max(size, 1), 100);

    // Linearly calculate the number of pairs based on size
    // size=1 -> 2 pairs, size=100 -> 12 pairs
    const numberOfPairs = Math.round(minPairs + ((maxPairs - minPairs) * (clampedSize - 1)) / 99);

    const shuffledCharacters = alphanumericCharacters.split('').sort(() => rng.number(-100, 100));

    // Get unique characters for the pairs
    const uniqueCharacters = shuffledCharacters.slice(0, Math.min(numberOfPairs, shuffledCharacters.length));

    // Create pairs by duplicating each character
    const pairs: string[] = [];
    uniqueCharacters.forEach((char) => {
      pairs.push(char, char);
    });

    // Shuffle the pairs to avoid having consecutive identical cards
    return pairs.sort(() => rng.number(-100, 100));
  }, [size, seed, minPairs, maxPairs]);
}
